import useSWR from 'swr';
import { Icon, Modal } from '@ontruck/orc';
import { getPlaceById } from 'modules/point-of-interest/service';

import {
  useVenueDetail,
  VenueAcceptedVehiclesRenderer,
  VenueBookingRenderer,
  VenueContactsRenderer,
  VenueDetail,
  VenueDetailContextProvider,
  VenueDriverInstructionsRenderer,
  VenueInternalInstructionsRenderer,
  VenueNameRenderer,
  VenueOpeningHoursRenderer,
  VenuePositionRenderer,
  VenueScheduleExceptionsRenderer,
  VenueVehicleRequirementsRenderer,
} from './venue-detail';

import VenueModificationsAlert from './VenueModificationsAlert';

import VenueCreateButton from './VenueCreateButton';

import { parseFromPelias } from './util';

const t = {
  debug: 'Source:',
  venue_name: 'Venue name',
  position: 'Position',
  address: 'Address',
  accepted_vehicles: 'Accepted vehicles',
  vehicle_requirements: 'Vehicle requirements',
  contacts: 'Contacts',
  booking: 'Booking',
  driver_instructions: 'Driver instructions',
  internal_instructions: 'Internal instructions',
  opening_hours: 'Opening hours',
  schedule_exceptions: 'Schedule exceptions',
  error: 'There was an error getting the Venue',
};

const VenueDetailsAndForm = ({ parsedData, error, isLoading }) => {
  const { isCreating, isCreatedConfirmation } = useVenueDetail();

  if (isCreatedConfirmation) {
    return (
      <div className="success-creation">
        <div>
          <Icon size="3rem" glyph="success" color="success900" />
        </div>
        <div className="success-title">Point of Interest created successfully!</div>
        <div className="success-subtitle">Your changes might take some time to be updated</div>
      </div>
    );
  }

  return (
    <div className="container" data-testid="VenueDetailModal">
      <div className="title-container">
        {!!parsedData?.venueName && <div className="title">{parsedData?.venueName}</div>}
        {!!parsedData?.venueRegion && <div className="subtitle">{parsedData?.venueRegion}</div>}
      </div>

      {!!error && (
        <div className="error">
          {t.error} {!!error.text && <>({error.text})</>}
        </div>
      )}

      {isLoading && !error && (
        <>
          <VenueDetail loading />
          <VenueDetail loading />
          <VenueDetail loading />
        </>
      )}

      {!isLoading && (
        <>
          <VenueModificationsAlert isCreating={isCreating} />
          <VenueDetail title={t.venue_name} fieldName="venueName" value={parsedData?.venueName}>
            <VenueNameRenderer />
          </VenueDetail>
          <VenueDetail title={t.position} value={parsedData?.position} fieldName="position">
            <VenuePositionRenderer />
          </VenueDetail>
          <VenueDetail
            title={t.accepted_vehicles}
            fieldName="acceptedVehicles"
            value={parsedData?.acceptedVehicles}
          >
            <VenueAcceptedVehiclesRenderer />
          </VenueDetail>
          <VenueDetail
            title={t.vehicle_requirements}
            fieldName="vehicleRequirements"
            value={parsedData?.vehicleRequirements}
          >
            <VenueVehicleRequirementsRenderer />
          </VenueDetail>
          <VenueDetail title={t.contacts} fieldName="contacts" value={parsedData?.contacts}>
            <VenueContactsRenderer />
          </VenueDetail>
          <VenueDetail title={t.booking} fieldName="booking" value={parsedData?.booking}>
            <VenueBookingRenderer />
          </VenueDetail>
          <VenueDetail
            title={t.driver_instructions}
            fieldName="driverInstructions"
            value={parsedData?.driverInstructions}
          >
            <VenueDriverInstructionsRenderer />
          </VenueDetail>
          <VenueDetail
            title={t.internal_instructions}
            fieldName="internalInstructions"
            value={parsedData?.internalInstructions}
          >
            <VenueInternalInstructionsRenderer />
          </VenueDetail>
          <VenueDetail
            title={t.opening_hours}
            fieldName="openingHours"
            value={parsedData?.openingHoursSchedules}
          >
            <VenueOpeningHoursRenderer timeFrames={parsedData?.defaultTimeFrames} />
          </VenueDetail>
          <VenueDetail title={t.schedule_exceptions}>
            <VenueScheduleExceptionsRenderer />
          </VenueDetail>

          {isCreating && <VenueCreateButton />}

          {parsedData?.sourceVersion && parsedData?.sourceOrigin && (
            <div className="develop-info">
              <a href={parsedData?.sourceURL} target="_blank" rel="noreferrer">
                {t.debug} v{parsedData.sourceVersion}-{parsedData?.sourceOrigin}
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const VenueDetailModal = ({ onClose, className, gid, addressId, isCreating }) => {
  const { data, error } = useSWR(gid ? gid : null, getPlaceById);

  const isLoading = typeof data === 'undefined' && !isCreating;
  const parsedData = parseFromPelias(data, gid);

  if (!gid && !isCreating) {
    return null;
  }

  return (
    <Modal
      show={true}
      onClose={onClose}
      size="M"
      visibleOverflow={false}
      withPadding={false}
      contentClassName={className}
    >
      <VenueDetailContextProvider
        venueId={gid}
        addressId={addressId}
        isCreating={isCreating}
        closeModal={onClose}
      >
        <VenueDetailsAndForm error={error} parsedData={parsedData} isLoading={isLoading} />
      </VenueDetailContextProvider>
    </Modal>
  );
};

export default VenueDetailModal;
